import { LocalTime } from '@js-joda/core';
import { useCallback, useMemo } from 'react';

import { Appointment, AvailabilitySimple } from '@dreryk/egabinet-appointment-frontend-api';

import {
   commonTranslationKeys,
   dateFormatter,
   PromiseVoidFunction,
   TranslatableText,
   useSingleInterpolatedTranslationKey,
   useTranslate,
} from '@dreryk/react-common';
import { ButtonVariant, ModalFooterItemConfiguration } from '@dreryk/react-components';

import { personnelTranslationKeys } from '../../translations';
import { RecurrencePeriod } from '../common/common.types';
import { getDefaultSlotDurationFixed } from '../common/fields/AvailabilityTimeSlot/AvailabilityTimeSlotFixed/AvailabilityTimeSlotFixed';

import { AvailabilityUpdateFormBean } from './availabilityUpdate.types';

export const useInitialValues = (selectedEvent: AvailabilitySimple): AvailabilityUpdateFormBean => {
   return useMemo<AvailabilityUpdateFormBean>(() => {
      return {
         availabilityDate: selectedEvent.availabilityDate,
         updateSingleOccurrence: false,
         recurrencePeriod: RecurrencePeriod.EVERY_WEEK,
         availabilityTypeId: selectedEvent.availabilityType,
         startTime: LocalTime.parse(selectedEvent.startTime.toString()),
         endTime: LocalTime.parse(selectedEvent.endTime.toString()),
         slotDurationFixed: getDefaultSlotDurationFixed(selectedEvent.slotDurationInMinutes ?? null),
         slotDurationManual: selectedEvent.slotDurationInMinutes ?? null,
         slotValidation: null,
         healthcareProviderLocation: undefined,
      };
   }, [
      selectedEvent.availabilityDate,
      selectedEvent.availabilityType,
      selectedEvent.endTime,
      selectedEvent.startTime,
      selectedEvent.slotDurationInMinutes,
   ]);
};

export const useCustomButtonsConfiguration = (
   onSubmit: PromiseVoidFunction,
   onRemove: PromiseVoidFunction
): {
   customButtonsConfiguration: ModalFooterItemConfiguration[];
} => {
   const customButtonsConfiguration: ModalFooterItemConfiguration[] = [
      {
         key: 'delete',
         label: commonTranslationKeys.remove,
         variant: ButtonVariant.SECONDARY,
         type: 'button',
         onClick: onRemove,
         testId: ' remove',
      },
      {
         key: 'confirm',
         label: commonTranslationKeys.save,
         testId: 'save',
         onClick: onSubmit,
      },
   ];
   return {
      customButtonsConfiguration: customButtonsConfiguration,
   };
};

export const useConfirmUpdateAvailabilityMessage = (
   updateSingleOccurrenceSelector: () => boolean,
   singleAvailability: boolean,
   appointmentForAvailabilityUpdate: Appointment | null
): TranslatableText => {
   const t = useTranslate();
   const formattedAppointmentDate = dateFormatter(appointmentForAvailabilityUpdate?.beginDate ?? null);
   const confirmUpdateSelectedRecurringAvailabilityIfAppointmentInRangeMessage = useSingleInterpolatedTranslationKey(
      personnelTranslationKeys.confirmUpdateSelectedRecurringAvailabilityIfAppointmentInRange,
      'appointmentDateInUpdatedAvailabilityDateRange',
      formattedAppointmentDate
   );

   if (singleAvailability) {
      return t(personnelTranslationKeys.confirmUpdateSelectedSingleAvailabilityIfAppointmentInRange);
   }
   if (updateSingleOccurrenceSelector()) {
      return t(personnelTranslationKeys.confirmUpdateSelectedSingleAvailabilityIfAppointmentInRange);
   }
   return t(confirmUpdateSelectedRecurringAvailabilityIfAppointmentInRangeMessage);
};

export const useConfirmRemovingAvailabilityMessage = (
   updateSingleOccurrenceSelector: () => boolean,
   singleAvailability: boolean,
   appointmentForAvailabilityUpdate: Appointment | null
): TranslatableText => {
   const t = useTranslate();
   const formattedAppointmentDate = dateFormatter(appointmentForAvailabilityUpdate?.beginDate ?? null);

   const confirmRemovingSelectedRecurringAvailabilityIfAppointmentInRangeMessage = useSingleInterpolatedTranslationKey(
      personnelTranslationKeys.confirmRemovingSelectedRecurringAvailabilityIfAppointmentInRange,
      'appointmentDateInUpdatedAvailabilityDateRange',
      formattedAppointmentDate
   );

   if (!formattedAppointmentDate) {
      return t(personnelTranslationKeys.confirmRemovingSelectedAvailability);
   }
   if (singleAvailability) {
      return t(personnelTranslationKeys.confirmRemovingSelectedSingleAvailabilityIfAppointmentInRange);
   }
   if (updateSingleOccurrenceSelector()) {
      return t(personnelTranslationKeys.confirmRemovingSelectedSingleAvailabilityIfAppointmentInRange);
   }
   return t(confirmRemovingSelectedRecurringAvailabilityIfAppointmentInRangeMessage);
};

export const useUpdateSingleOccurrenceSupplier = (getValue: (name: string) => unknown): (() => boolean) => {
   return useCallback((): boolean => {
      /*
       * Castowanie wartości zwracanej z getValue do boolean jest konieczne, ponieważ jest ona zwracana z typem 'unknown'.
       * useFormController nie przypisuje prawidłowo typów do wartości formularza.
       * Prawdopodobna przyczyna opisana w https://dreryk.atlassian.net/browse/DRWEB-3446
       * Castowanie to jest bezpieczne, ponieważ updateSingleOccurrence to pole typu checkbox, które zwraca boolean
       */
      return getValue('updateSingleOccurrence') as boolean;
   }, [getValue]);
};
